'use client';

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Button, Text, toasterApi } from '@terra/ui-design-system';
import { HStack, VStack } from '@terra/ui-styled-system';
import { getCurrentRootDomain } from '../../utils/environmentDetection';
import {
  PORTAL_REGION,
  PORTAL_REGION_CONFIG,
  PORTAL_REGION_ORDER,
  getPortalRegionPortalAppUrl,
  resolvePortalRegionFromRootDomain,
  shouldShowSignInRegionSelector,
  type PortalRegion,
} from '../../utils/portalRegions';
import { PortalRegionFlagButton } from './PortalRegionFlagButton';

const REGION_LABEL_KEY: Record<PortalRegion, 'usa' | 'europe'> = {
  [PORTAL_REGION.USA]: 'usa',
  [PORTAL_REGION.EUROPE]: 'europe',
};

const REGION_FLAG_ALT_KEY: Record<
  PortalRegion,
  'usaFlagAlt' | 'europeFlagAlt'
> = {
  [PORTAL_REGION.USA]: 'usaFlagAlt',
  [PORTAL_REGION.EUROPE]: 'europeFlagAlt',
};

export function SignInRegionSelector() {
  const t = useTranslations('signIn');
  const tCommon = useTranslations('common');
  const searchParams = useSearchParams();
  const [rootDomain, setRootDomain] = useState('');
  const [hasElectronDomainApi, setHasElectronDomainApi] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  useEffect(() => {
    const loadRootDomain = async () => {
      if (window.electronAPI?.domain?.get) {
        setHasElectronDomainApi(true);
        try {
          const domainState = await window.electronAPI.domain.get();
          setRootDomain(domainState.rootDomain);
          return;
        } catch (error) {
          console.error('Failed to load domain from Electron:', error);
        }
      }

      setRootDomain(getCurrentRootDomain());
    };

    void loadRootDomain();
  }, []);

  const currentRegion = resolvePortalRegionFromRootDomain(rootDomain);

  const handleSelectRegion = useCallback(
    async (targetRegion: PortalRegion) => {
      if (targetRegion === currentRegion || isSwitching) {
        return;
      }

      const targetConfig = PORTAL_REGION_CONFIG[targetRegion];
      const regionLabel = t(`region.${REGION_LABEL_KEY[targetRegion]}`);

      if (window.electronAPI?.domain?.set) {
        setIsSwitching(true);
        toasterApi.info({
          title: t('toasts.regionSwitching.title'),
          description: t('toasts.regionSwitching.description', {
            region: regionLabel,
          }),
        });

        try {
          const result = await window.electronAPI.domain.set(
            targetConfig.rootDomain,
          );
          if (!result.success) {
            throw new Error(
              result.error ?? t('toasts.regionSwitchFailed.description'),
            );
          }
        } catch (error) {
          setIsSwitching(false);
          toasterApi.error({
            title: t('toasts.regionSwitchFailed.title'),
            description:
              error instanceof Error
                ? error.message
                : t('toasts.regionSwitchFailed.description'),
          });
        }
        return;
      }

      const signInUrl = new URL(
        '/sign-in',
        getPortalRegionPortalAppUrl(targetRegion),
      );
      const callbackUrl = searchParams.get('callbackUrl');
      if (callbackUrl) {
        signInUrl.searchParams.set('callbackUrl', callbackUrl);
      }
      window.location.assign(signInUrl.toString());
    },
    [currentRegion, isSwitching, searchParams, t],
  );

  if (
    !shouldShowSignInRegionSelector({
      hasElectronDomainApi,
      rootDomain,
    })
  ) {
    return null;
  }

  const currentRegionLabel = currentRegion
    ? t(`region.${REGION_LABEL_KEY[currentRegion]}`)
    : null;

  return (
    <VStack gap={2} w="full" alignItems="center">
      {!isExpanded ? (
        <Text
          fontSize="xs"
          color="fg.muted"
          textAlign="center"
          cursor="pointer"
          onClick={() => setIsExpanded(true)}
        >
          {t('region.changeLink')}
        </Text>
      ) : (
        <VStack gap={2} w="full" alignItems="center">
          {currentRegionLabel ? (
            <Text fontSize="xs" color="fg.muted" textAlign="center">
              {t('region.current', { region: currentRegionLabel })}
            </Text>
          ) : null}
          <HStack gap={3} w="full" justify="center" alignItems="flex-start">
            {PORTAL_REGION_ORDER.map((region) => {
              const isSelected = region === currentRegion;
              const regionLabel = t(`region.${REGION_LABEL_KEY[region]}`);
              const flagAlt = t(`region.${REGION_FLAG_ALT_KEY[region]}`);

              return (
                <PortalRegionFlagButton
                  key={region}
                  region={region}
                  isSelected={isSelected}
                  disabled={isSwitching}
                  regionLabel={regionLabel}
                  flagAlt={flagAlt}
                  onSelect={() => void handleSelectRegion(region)}
                />
              );
            })}
          </HStack>
          <Button
            size="xs"
            variant="ghost"
            disabled={isSwitching}
            onClick={() => setIsExpanded(false)}
          >
            {tCommon('cancel')}
          </Button>
        </VStack>
      )}
    </VStack>
  );
}
